import i18next from "i18next"
import type { TOptions } from "i18next"
import type i18nJson from "i18n.json"

/**
 * i18n.json の翻訳キーの型
 * この型により、存在しないキーを使用するとコンパイルエラーになります
 */
export type TranslationKey = keyof typeof i18nJson

/**
 * インターポレーション用のパラメータ型
 */
type InterpolationParams = Record<string, string | number>

/**
 * i18next.t() の戻り値を安全に文字列に変換する
 */
function ensureString(result: unknown, key: string): string {
  if (typeof result === "string") {
    return result
  }
  // 文字列以外が返された場合（通常は発生しないが、型安全のため）
  console.error(`[i18n] Translation for "${key}" did not return string:`, result)
  return String(result)
}

/**
 * 型安全な翻訳関数
 *
 * i18n.json に定義されていないキーを使用するとコンパイルエラーになります。
 *
 * @example
 * // 正常: i18n.json に定義されているキー
 * t("Published")
 *
 * // エラー: i18n.json に定義されていないキー
 * t("NonExistentKey") // TypeScript エラー
 *
 * // インターポレーション付き
 * t("{{count}} changes since last publish", { count: 5 })
 */
export function t(key: TranslationKey, params?: InterpolationParams): string {
  const result = i18next.t(key, params as TOptions)
  return ensureString(result, key)
}

/**
 * 動的なキーを翻訳する関数
 *
 * memberRoleDescriptions.ts のように、オブジェクトのプロパティから
 * 翻訳キーを取得する場合に使用します。
 *
 * 注意: この関数は型チェックをバイパスするため、
 * 可能な限り t() 関数を使用してください。
 *
 * @example
 * // 動的キーの場合のみ使用
 * tDynamic(roleDescription.titleKey)
 */
export function tDynamic(key: string, params?: InterpolationParams): string {
  const result = i18next.t(key, params as TOptions)

  // 開発時: 翻訳リソースにキーが登録されていない場合のみ警告
  // (英語モードでは値=キーになる設計のため、result === key での判定は誤検知になる)
  if (process.env.NODE_ENV === "development" && !i18next.exists(key)) {
    console.warn(`[i18n] Missing translation key: "${key}"`)
  }

  return ensureString(result, key)
}
